import { useInquirySender } from "@/hooks/use-inquiry-sender";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Separator } from "@/components/ui/separator";
import type { Conversation } from "@/lib/types";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useTimeout, useWindowSize } from "react-use";
import Confetti from "react-confetti";
import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { buildChatUrl } from "@/lib/url";

export type ListingInquirySentProps = {
  conversation: Conversation;
};

export function ListingInquirySent({ conversation }: ListingInquirySentProps) {
  const t = useTranslations();
  const { delegated, user } = useInquirySender();

  const [isConfettiComplete] = useTimeout(5000);
  const { width, height } = useWindowSize();

  const sentAt = useMemo(() => {
    return formatDistanceToNow(conversation.createdAt, {
      addSuffix: true,
    });
  }, [conversation.createdAt]);

  return (
    <div className="flex flex-col relative overflow-hidden">
      <div className="px-6 py-8 flex flex-col items-center gap-4">
        <div className="size-10 rounded-full bg-theme-green text-white flex items-center justify-center">
          <Icon icon="CheckSolid" />
        </div>

        <div className="text-2xl font-semibold">
          {t("saas.listing.sidebar.sent.title")}
        </div>

        <div className="text-center">
          {t("saas.listing.sidebar.sent.description")}
        </div>
      </div>

      <Separator />

      <div className="p-6 flex flex-col gap-4">
        <div className="text-xs text-center">
          {delegated
            ? `Replies go to ${user?.name ?? "the selected broker"}’s conversation inbox.`
            : t("saas.listing.sidebar.sent.footer.blurb")}
        </div>

        {!delegated && (
          <Button asChild>
            <Link href={buildChatUrl(conversation)}>
              {t("saas.listing.sidebar.sent.footer.open")}
            </Link>
          </Button>
        )}

        <div className="text-xs text-center text-muted-foreground">
          {t("saas.listing.sidebar.sent.footer.sentAt", {
            sentAt,
          })}
        </div>
      </div>

      <Confetti recycle={!isConfettiComplete} width={width} height={height} />
    </div>
  );
}
